import { useState } from "react";
import axios from "axios";
import { ArrowDownToLine, FileText, AlertCircle, PenTool, ClipboardCheck, CheckCircle2 } from "lucide-react";
import { API, portalHeaders } from "../helpers.js";
import DownloadBtn from "../components/DownloadBtn.jsx";
import SignatureCanvasModal from "../components/SignatureCanvasModal.jsx";

const TYPE_LABELS = {
  document: "Document", demande_document: "Demande",
  demande_manquant: "Manquant", signature: "Signature",
};
const TYPE_COLORS = {
  document: "bg-blue-100 text-blue-700",
  demande_document: "bg-amber-100 text-amber-700",
  demande_manquant: "bg-red-100 text-red-700",
  signature: "bg-purple-100 text-purple-700",
};

export default function ReceivedTab({ receivedComms, primaryColor, onReload }) {
  const [signing, setSigning] = useState(null); // {docId, title}

  const markRead = async (commId) => {
    try {
      await axios.put(`${API}/portal/communications/${commId}/read`, {}, { headers: portalHeaders() });
      onReload();
    } catch { /* silent */ }
  };

  const getIcon = (type) => {
    if (type === "signature") return <PenTool className="w-4 h-4 text-purple-600" />;
    if (type === "demande_manquant") return <AlertCircle className="w-4 h-4 text-red-600" />;
    if (type === "demande_document") return <ClipboardCheck className="w-4 h-4 text-amber-600" />;
    return <FileText className="w-4 h-4 text-blue-600" />;
  };

  return (
    <div className="space-y-3" data-testid="tab-recu">
      <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
        <ArrowDownToLine className="w-4 h-4" /> Documents reçus de l'équipe
      </h3>

      {!receivedComms.length ? (
        <div className="bg-white rounded-xl shadow-sm border p-10 text-center text-gray-400">
          <ArrowDownToLine className="w-8 h-8 mx-auto mb-2 opacity-40" />
          <p className="text-sm">Aucun document recu pour le moment</p>
        </div>
      ) : receivedComms.map(comm => (
        <div key={comm.id}
          className={`bg-white rounded-xl shadow-sm border p-4 ${!comm.read ? "border-l-4 border-l-blue-500" : ""}`}
          data-testid={`recu-${comm.id}`}
          onClick={() => !comm.read && markRead(comm.id)}>
          <div className="flex items-start gap-3">
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
              comm.type === "signature" ? "bg-purple-100" :
              comm.type === "demande_manquant" ? "bg-red-100" :
              comm.type === "demande_document" ? "bg-amber-100" : "bg-blue-100"
            }`}>
              {getIcon(comm.type)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${TYPE_COLORS[comm.type] || "bg-gray-100 text-gray-600"}`}>
                  {TYPE_LABELS[comm.type] || comm.type}
                </span>
                {!comm.read && <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-blue-500 text-white">Nouveau</span>}
              </div>
              <p className="text-sm font-semibold mt-1">{comm.title}</p>
              {comm.message && <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{comm.message}</p>}
              <p className="text-[10px] text-gray-400 mt-1">
                {new Date(comm.created_at).toLocaleDateString("fr-CA")} a {new Date(comm.created_at).toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })}
              </p>
            </div>
          </div>
          {(comm.attachments || []).length > 0 && (
            <div className="ml-11 mt-2 space-y-1.5">
              {comm.attachments.map(att => (
                <div key={att.id} className="flex items-center gap-2 flex-wrap">
                  <DownloadBtn fileUrl={att.url} filename={att.filename} primaryColor={primaryColor}
                    resourceType="communication" resourceId={att.id} label={att.filename} />
                  {/* Iter100 — Bouton Signer pour les comms de type signature non signees */}
                  {comm.type === "signature" && !att.signed && !comm.signed && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSigning({ docId: att.id, title: att.filename });
                      }}
                      className="inline-flex items-center gap-1 text-xs bg-purple-600 text-white px-2.5 py-1 rounded-md hover:bg-purple-700 transition"
                      data-testid={`sign-btn-${att.id}`}
                    >
                      <PenTool className="w-3 h-3" /> Signer
                    </button>
                  )}
                  {(att.signed || comm.signed) && (
                    <span className="inline-flex items-center gap-1 text-xs bg-emerald-100 text-emerald-700 px-2 py-1 rounded-md" data-testid={`signed-badge-${att.id}`}>
                      <CheckCircle2 className="w-3 h-3" /> Signé
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {signing && (
        <SignatureCanvasModal
          documentId={signing.docId}
          documentTitle={signing.title}
          onSigned={() => onReload()}
          onClose={() => setSigning(null)}
        />
      )}
    </div>
  );
}
